import React, { useRef, useState, useEffect, useContext, useCallback } from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dialog } from 'primereact/dialog';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import axios from 'axios';
import { BarrioContext } from '../components/BarrioContext';
import { useAuth } from '../components/AuthContext';

const initialFormData = {
    itemMantenimientoId: 0,
    nombre: '',
    descripcion: '',
    activo: true,
};

const ConfiguracionMantenimiento = () => {
    const toast = useRef(null);
    const HOST_API = process.env.REACT_APP_URL;
    const { barrio } = useContext(BarrioContext);
    const { administracionId } = useAuth();

    const [formData, setFormData] = useState(initialFormData);
    const [visible, setVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState([]);

    const handleChange = (campo, valor) => setFormData(prev => ({ ...prev, [campo]: valor }));

    const fetchItems = useCallback(async () => {
        if (!barrio?.id) return;
        setLoading(true);
        try {
            const { data } = await axios.get(
                `${HOST_API}api/itemsMantenimiento/GetItems?administracionId=${administracionId}&barrioId=${barrio.id}`
            );
            setItems(data);
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar los ítems', life: 3000 });
        } finally {
            setLoading(false);
        }
    }, [barrio?.id, HOST_API, administracionId]);

    useEffect(() => { fetchItems(); }, [fetchItems]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.nombre.trim()) {
            toast.current.show({ severity: 'warn', summary: 'Atención', detail: 'El nombre es obligatorio', life: 3000 });
            return;
        }
        try {
            await axios.post(`${HOST_API}api/itemsMantenimiento/SaveItem`, {
                ...formData,
                barrioId: barrio.id,
                administracionId,
            });
            setVisible(false);
            fetchItems();
            toast.current.show({
                severity: 'success',
                summary: formData.itemMantenimientoId ? 'Actualizado' : 'Creado',
                detail: `Ítem ${formData.itemMantenimientoId ? 'actualizado' : 'creado'} correctamente`,
                life: 2500,
            });
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo guardar el ítem', life: 3000 });
        }
    };

    const handleEdit = (row) => {
        setFormData({
            itemMantenimientoId: row.itemMantenimientoId,
            nombre:               row.nombre || '',
            descripcion:          row.descripcion || '',
            activo:               row.activo,
        });
        setVisible(true);
    };

    const handleDelete = (id, nombre) => {
        confirmDialog({
            message: `¿Estás seguro de eliminar "${nombre}"?`,
            header: 'Confirmar eliminación',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Eliminar',
            rejectLabel: 'Cancelar',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    await axios.delete(`${HOST_API}api/itemsMantenimiento/DeleteItem?itemMantenimientoId=${id}&administracionId=${administracionId}`);
                    fetchItems();
                    toast.current.show({ severity: 'success', summary: 'Eliminado', detail: 'Ítem eliminado', life: 2500 });
                } catch {
                    toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo eliminar', life: 3000 });
                }
            },
        });
    };

    const descripcionTemplate = (row) =>
        row.descripcion
            ? row.descripcion.length > 60 ? row.descripcion.substring(0, 60) + '…' : row.descripcion
            : <span style={{ color: '#98a2b3' }}>—</span>;

    return (
        <>
            <h4>Configuración Mantenimiento</h4>
            <br />

            <ConfirmDialog />
            <Toast ref={toast} />
            <Dialog visible={loading} modal closable={false} showHeader={false} className="backdrop-dialog">
                <ProgressSpinner style={{ width: 60, height: 60 }} />
            </Dialog>

            <div className="flex align-items-center gap-3 mb-3">
                <Button
                    label="Nuevo Ítem"
                    icon="pi pi-plus"
                    onClick={() => { setFormData(initialFormData); setVisible(true); }}
                    disabled={!barrio?.id}
                />
                {!barrio?.id && (
                    <span style={{ color: '#f04438', fontSize: '0.85rem' }}>
                        Seleccioná un barrio para continuar
                    </span>
                )}
            </div>

            <Dialog
                header={formData.itemMantenimientoId ? 'Editar Ítem' : 'Nuevo Ítem'}
                visible={visible}
                style={{ width: '480px' }}
                breakpoints={{ '960px': '100vw' }}
                modal
                onHide={() => setVisible(false)}
            >
                <form onSubmit={handleSubmit}>
                    <div className="flex flex-column gap-3 mt-2">

                        <div>
                            <label style={labelStyle}>Nombre <span style={{ color: '#f04438' }}>*</span></label>
                            <InputText
                                value={formData.nombre}
                                onChange={(e) => handleChange('nombre', e.target.value)}
                                className="p-inputtext-sm w-full"
                                placeholder="Ej: Ascensor, Bomba de agua, Portón eléctrico"
                                maxLength={100}
                            />
                        </div>

                        <div>
                            <label style={labelStyle}>Descripción <span style={optStyle}>(opcional)</span></label>
                            <InputTextarea
                                value={formData.descripcion}
                                onChange={(e) => handleChange('descripcion', e.target.value)}
                                rows={3}
                                autoResize
                                className="w-full"
                                placeholder="Ubicación, detalles del ítem, etc."
                                maxLength={500}
                            />
                        </div>

                        <div className="flex justify-content-end gap-2 mt-2">
                            <Button label="Cancelar" type="button" severity="secondary" text onClick={() => setVisible(false)} />
                            <Button label="Guardar" icon="pi pi-save" type="submit" />
                        </div>

                    </div>
                </form>
            </Dialog>

            <div className="grid">
                <div className="col-12">
                    <DataTable
                        value={items}
                        paginator
                        rows={10}
                        showGridlines
                        rowsPerPageOptions={[5, 10, 25]}
                        responsiveLayout="scroll"
                        emptyMessage="No hay ítems de mantenimiento registrados para este barrio"
                        className="p-datatable-gridlines bg-white"
                        sortField="nombre"
                        sortOrder={1}
                    >
                        <Column field="nombre" header="Nombre" sortable style={{ fontWeight: 500 }} />
                        <Column field="descripcion" header="Descripción" body={descripcionTemplate} />
                        <Column
                            header="Estado"
                            style={{ width: '8rem' }}
                            body={(row) => row.activo
                                ? <span className="customer-badge status-ingreso">Activo</span>
                                : <span className="customer-badge status-renewal" style={{ background: '#fde8e8', color: '#c0392b' }}>No Activo</span>
                            }
                        />
                        <Column
                            header="Acciones"
                            style={{ width: '8rem' }}
                            body={(row) => (
                                <div className="flex gap-2 justify-content-center">
                                    <Button icon="pi pi-pencil" rounded text severity="warning" onClick={() => handleEdit(row)} tooltip="Editar" />
                                    <Button icon="pi pi-trash" rounded className="btn-delete" onClick={() => handleDelete(row.itemMantenimientoId, row.nombre)} tooltip="Eliminar" />
                                </div>
                            )}
                        />
                    </DataTable>
                </div>
            </div>
        </>
    );
};

const labelStyle = { display: 'block', marginBottom: '0.35rem', fontWeight: 500, color: '#344054', fontSize: '0.87rem' };
const optStyle   = { color: '#98a2b3', fontWeight: 400 };

export default ConfiguracionMantenimiento;
